


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type { StandardReportCar, StandardReport } from '@/models/externalReports/standard/spStandardReport';
import type {
  ExternalReportsLabelSp,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import type {
  StandardReportOperationEndTsUpdateParams,
} from '@/models/apis/externalReports/standard/standardReportRequest';
import standardReportApi from '@/apis/externalReports/standard/standard_report';
import useSpeech from '@/composables/useSpeech';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { useRoute } from '@/hooks/useRoute';

interface WorkingState {
  showConfirmStartWorkingModal: boolean;
  showConfirmTemporarilyStopWorkingModal: boolean;
  showConfirmStopWorkingModal: boolean;
  isSuspending: boolean;
  standardReport: StandardReport;
}
export default defineComponent({
  props: {
    standardCar: {
      type: Object as PropType<StandardReportCar>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  emits: [
    'updateCommonAndCarStatus',
    'suspendWork',
    'editContentAndRoad',
    'showErrorOnCommon',
    'addPhotoMemo',
  ],
  setup(props, { emit }) {
    const state = reactive<WorkingState>({
      showConfirmStartWorkingModal: false,
      showConfirmTemporarilyStopWorkingModal: false,
      showConfirmStopWorkingModal: false,
      isSuspending: false,
      standardReport: props.standardCar.report,
    });
    const { doSpeech } = useSpeech();
    const { router } = useRoute();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const spLabels = computed<ExternalReportsLabelSp>(() => {
      return props.userSettings.sp;
    });

    const startWorking = () => {
      doSpeech(spLabels.value.message.confirm_resume_work);
      state.isSuspending = false;
      state.showConfirmStartWorkingModal = false;
      emit('updateCommonAndCarStatus', props.userSettings.choices.status.working.key, state.standardReport);
    };

    const temporarilyStopWorking = () => {
      state.isSuspending = true;
      state.showConfirmTemporarilyStopWorkingModal = false;
      doSpeech(spLabels.value.message.confirm_pause_work);
      emit('suspendWork', props.userSettings.choices.status.running.key, state.standardReport);
    };

    const stopWorking = async() => {
      const params: StandardReportOperationEndTsUpdateParams = {
        operation_end_ts: new Date(),
      };
      state.showConfirmStopWorkingModal = false;
      await doRequest(async() => {
        state.standardReport = await standardReportApi.updateStandardReportEnd(state.standardReport.id, params);
      }, '作業終了に失敗しました。再度操作を行ってください。');
      doSpeech(spLabels.value.message.confirm_end_work);
      emit('suspendWork', props.userSettings.choices.status.running.key, state.standardReport);
    };

    const addPhotoMemo = () => {
      emit('addPhotoMemo');
    };

    const editContentAndRoad = () => {
      emit('editContentAndRoad');
    };

    onMounted(() => {
      if (state.standardReport.id === 0) {
        router.push({name: 'Login'});
      }
    });

    return {
      state,
      isRequesting,
      spLabels,
      addPhotoMemo,
      editContentAndRoad,
      startWorking,
      stopWorking,
      temporarilyStopWorking,
    };
  },
});
